import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import toast from "react-hot-toast";
import { TriangleAlert } from "lucide-react";
import { useGetCurrentLocalFacilityId } from "@/modules/facility/facilityHooks";
import { Skeleton } from "@/components/ui/skeleton";
import { ConfirmDialog } from "../../common/ConfirmDialog";
import { RefreshOverlay } from "../../common/RefreshOverlay";
import { TeamsBoard } from "../../teams/TeamsBoard";
import {
  boardStats,
  fullTimePlayers,
  isSupportedTeamSize,
  DEFAULT_TEAM_SIZE,
  MAX_TEAM_SIZE,
  applyRosterShells,
  isLocalTeamId,
  placeOnTeam,
  renameTeamDraft,
  takeOffTeam,
  toBoardTeams,
  unassignedPlayers,
  type BoardId,
  type BoardTeam,
} from "../../teams/teamsBoardModel";
import {
  useAssignTeamPlayersMutation,
  useCreateLeagueTeamMutation,
  useDeleteLeagueTeamMutation,
  useGetLeagueTeamsQuery,
  useRemoveTeamPlayerMutation,
  useRenameLeagueTeamMutation,
  useWithdrawLeagueTeamMutation,
} from "../../../api/endpoints/teams";
import { useGetLeagueRosterQuery } from "../../../api/endpoints/roster";
import { useGetLeagueQuery } from "../../../api/endpoints/leagueDetail";
import { useGetLeagueRoundsQuery } from "../../../api/endpoints/rounds";
import { Clubhouse_LeagueV2Status } from "../../../enums";
import {
  anyLeagueRoundStarted,
  LEAGUE_TEAMS_LOCK_MESSAGE,
  ROUND_TEAMS_LOCK_MESSAGE,
} from "../../teams/teamEditPolicy";
import {
  canEditTeamStructureStepByStep,
  canWithdrawTeam,
  isMatchPlayLocked,
  MATCH_PLAY_LOCK_MESSAGE,
  WITHDRAW_TEAM_CONFIRMATION,
} from "../../../rosterChangePolicy";

const toastOpts = {
  position: "top-center" as const,
  className: "text-sm poppins",
};

/**
 * Teams manager for Advanced Setup and the league Participants tab.
 * A round page passes `readOnly`: membership is never edited there.
 * On the league, edits stay open until a round has started.
 *
 * Shells and assignments are edited in memory. The server is written only when
 * the step is saved or left, so the board never waits on a create or a drop.
 *
 * Empty shells stay local. A team is created when it actually has a player.
 */
export type TeamsTabHandle = {
  /** Write the in-memory board to the server. Safe to call more than once. */
  persistTeams: () => Promise<void>;
};

export const TeamsTab = forwardRef<
  TeamsTabHandle,
  {
    leagueId: string;
    hideHeading?: boolean;
    facilityId?: string;
    readOnly?: boolean;
    /** True while the in-memory board is being written before leaving the step. */
    isSaving?: boolean;
  }
>(function TeamsTab(
  {
    leagueId,
    hideHeading = false,
    facilityId: facilityIdProp,
    readOnly = false,
    isSaving = false,
  },
  ref,
) {
  const currentFacilityId = useGetCurrentLocalFacilityId();
  const facilityId = facilityIdProp ?? currentFacilityId;
  const scope = { facilityId: facilityId ?? "", leagueId };
  const skip = !facilityId;

  const { data: teamsData, isLoading } = useGetLeagueTeamsQuery(scope, {
    skip,
  });
  const { data: rosterData, isLoading: rosterLoading } = useGetLeagueRosterQuery(
    scope,
    { skip },
  );
  // Team size belongs to the league, not the board. An omitted size means
  // DEFAULT_TEAM_SIZE — see that constant for why absence is a product
  // constraint here and not a guess, and for when to retire it.
  const { data: leagueData, isLoading: leagueLoading } = useGetLeagueQuery(
    leagueId,
    { skip: !leagueId },
  );
  // A round page is always view-only, so it doesn't need this. On the league
  // page, membership stays editable until a round has actually started.
  const { data: roundsData, isLoading: roundsLoading } =
    useGetLeagueRoundsQuery(scope, { skip: skip || readOnly });

  // Which of three different rules closes this board, in the order they bite.
  // The board itself only takes one sentence, and the operator needs the one
  // that tells them what to do instead — "use a substitute" and "add a whole
  // team" are different instructions, and "a round has started" is neither.
  //
  // Every granular write this board makes (create, delete, assign, remove) is
  // refused by the API under these rules, and `persistTeams` is gated on
  // `editable`, so getting this right is also what stops the unmount flush from
  // firing a batch of calls that are certain to fail.
  const detail = leagueData?.leagueV2Detail;
  const roundStarted = anyLeagueRoundStarted(roundsData);
  const policyContext = {
    // A round that has started is proof the league was finalized — rounds are
    // only materialized at finalize — so it counts even if the league read is
    // stale or omitted the status. Deciding this from `status` alone would leave
    // a started board fully editable whenever that field did not arrive.
    status: roundStarted ? Clubhouse_LeagueV2Status.ACTIVE : detail?.status,
    type: detail?.leagueType,
    scoring: detail?.scoring,
    matchPlayBegun: detail?.playBegun === true || roundStarted,
  };
  const lockMessage = readOnly
    ? ROUND_TEAMS_LOCK_MESSAGE
    : isMatchPlayLocked(policyContext)
      ? MATCH_PLAY_LOCK_MESSAGE
      : canEditTeamStructureStepByStep(policyContext)
        ? undefined
        : LEAGUE_TEAMS_LOCK_MESSAGE;
  const teamSize = leagueData?.leagueV2Detail?.teamSize ?? DEFAULT_TEAM_SIZE;
  const teamSizeUsable = isSupportedTeamSize(teamSize);

  const players = useMemo(() => fullTimePlayers(rosterData), [rosterData]);
  const teams = useMemo(
    () =>
      teamSizeUsable ? toBoardTeams(teamsData?.teams, players, teamSize) : [],
    [teamsData, players, teamSize, teamSizeUsable],
  );
  const editable = !readOnly && !lockMessage && teamSizeUsable;
  const withdrawTeams = !readOnly && canWithdrawTeam(policyContext);
  // `null` until the operator edits. Until then the board is the server teams
  // plus the empty shells the roster still needs — those shells are not saved.
  const [draft, setDraft] = useState<BoardTeam[] | null>(null);
  const displayed = useMemo(
    () =>
      editable
        ? applyRosterShells(draft ?? teams, players.length, teamSize)
        : teams,
    [editable, draft, teams, players.length, teamSize],
  );
  const unassigned = useMemo(
    () => unassignedPlayers(players, displayed),
    [players, displayed],
  );

  const [teamToDelete, setTeamToDelete] = useState<BoardTeam | null>(null);
  const [teamToWithdraw, setTeamToWithdraw] = useState<BoardTeam | null>(null);

  const [createTeam] = useCreateLeagueTeamMutation();
  const [renameTeam] = useRenameLeagueTeamMutation();
  const [deleteTeam, { isLoading: deleting }] = useDeleteLeagueTeamMutation();
  const [assignPlayers] = useAssignTeamPlayersMutation();
  const [removePlayer] = useRemoveTeamPlayerMutation();
  const [withdrawTeam, { isLoading: withdrawing }] = useWithdrawLeagueTeamMutation();

  const report = (message: string) => (err: unknown) => {
    console.error(message, err);
    toast.error(message, toastOpts);
  };

  const draftRef = useRef(draft);
  const teamsRef = useRef(teams);
  const displayedRef = useRef(displayed);
  const baselineRef = useRef(teams);
  const editableRef = useRef(editable);
  const persistLock = useRef<Promise<void> | null>(null);
  draftRef.current = draft;
  teamsRef.current = teams;
  displayedRef.current = displayed;
  editableRef.current = editable;
  if (draft == null) baselineRef.current = teams;

  const editDraft = (mutate: (current: BoardTeam[]) => BoardTeam[]) => {
    setDraft((current) =>
      mutate(
        applyRosterShells(
          current ?? teamsRef.current,
          players.length,
          teamSize,
        ),
      ),
    );
  };

  const persistTeams = async () => {
    if (!editableRef.current) return;
    if (persistLock.current) return persistLock.current;
    const run = (async () => {
      const current = displayedRef.current;
      const baseline = baselineRef.current;
      const facility = facilityId ?? "";
      const savedIds = new Map<string, BoardId>();
      try {
        for (const team of baseline) {
          if (current.some((item) => String(item.teamId) === String(team.teamId))) {
            continue;
          }
          await deleteTeam({ facilityId: facility, leagueId, teamId: team.teamId }).unwrap();
        }
        for (const team of current) {
          const occupied = team.slots.flatMap((slot) => (slot ? [slot] : []));
          if (isLocalTeamId(team.teamId)) {
            if (occupied.length === 0) continue;
            const created = await createTeam({
              facilityId: facility,
              leagueId,
              body: { teamName: team.name },
            }).unwrap();
            const teamId = created?.teamId;
            if (teamId == null) {
              throw new Error("Create team did not return an id");
            }
            savedIds.set(String(team.teamId), teamId);
            const assigned = await assignPlayers({
              facilityId: facility,
              leagueId,
              teamId,
              body: { userIds: occupied.map((player) => player.userId) },
            }).unwrap();
            if (assigned?.rejected?.length) {
              toast.error(
                "That player couldn't be added to this team.",
                toastOpts,
              );
            }
            continue;
          }
          const previous = baseline.find(
            (item) => String(item.teamId) === String(team.teamId),
          );
          if (previous && previous.name !== team.name) {
            await renameTeam({
              facilityId: facility,
              leagueId,
              teamId: team.teamId,
              body: { teamName: team.name },
            }).unwrap();
          }
          const previousIds = new Set(
            (previous?.slots ?? []).flatMap((slot) =>
              slot ? [String(slot.userId)] : [],
            ),
          );
          for (const slot of previous?.slots ?? []) {
            if (!slot) continue;
            if (occupied.some((player) => String(player.userId) === String(slot.userId))) {
              continue;
            }
            await removePlayer({
              facilityId: facility,
              leagueId,
              teamId: team.teamId,
              userId: slot.userId,
            }).unwrap();
          }
          const added = occupied.filter(
            (player) => !previousIds.has(String(player.userId)),
          );
          if (added.length > 0) {
            const assigned = await assignPlayers({
              facilityId: facility,
              leagueId,
              teamId: team.teamId,
              body: { userIds: added.map((player) => player.userId) },
            }).unwrap();
            if (assigned?.rejected?.length) {
              toast.error(
                "That player couldn't be added to this team.",
                toastOpts,
              );
            }
          }
        }
        const settled = current.map((team) => {
          const teamId = savedIds.get(String(team.teamId)) ?? team.teamId;
          return { ...team, teamId };
        });
        baselineRef.current = settled.filter((team) => !isLocalTeamId(team.teamId));
        displayedRef.current = settled;
        setDraft(settled);
      } catch (err) {
        report("Couldn't save the teams. Please try again.")(err);
        throw err;
      }
    })();
    persistLock.current = run;
    try {
      await run;
    } finally {
      persistLock.current = null;
    }
  };

  const persistTeamsRef = useRef(persistTeams);
  persistTeamsRef.current = persistTeams;
  useImperativeHandle(ref, () => ({
    persistTeams: () => persistTeamsRef.current(),
  }));

  useEffect(() => {
    return () => {
      void persistTeamsRef.current().catch(() => undefined);
    };
  }, []);

  const handleAddTeam = () => undefined;

  const handleRename = (teamId: BoardId, teamName: string) => {
    editDraft((current) => renameTeamDraft(current, teamId, teamName));
  };

  const handleWithdraw = async () => {
    if (teamToWithdraw == null) return;
    try {
      await withdrawTeam({
        facilityId: facilityId ?? "",
        leagueId,
        teamId: teamToWithdraw.teamId,
      }).unwrap();
      toast.success("Team withdrawn", toastOpts);
      setTeamToWithdraw(null);
    } catch (err) {
      console.error("withdrawTeam failed", err);
      toast.error("Couldn't withdraw the team. Please try again.", toastOpts);
    }
  };

  const handleDelete = () => {
    if (teamToDelete == null) return;
    const teamId = teamToDelete.teamId;
    editDraft((current) =>
      applyRosterShells(
        current.filter((team) => String(team.teamId) !== String(teamId)),
        players.length,
        teamSize,
      ),
    );
    setTeamToDelete(null);
  };

  const handleAssign = (teamId: BoardId, userId: BoardId) => {
    const player = players.find((item) => item.userId === userId);
    if (!player) return;
    editDraft((current) => placeOnTeam(current, teamId, player, teamSize));
  };

  const handleRemovePlayer = (teamId: BoardId, userId: BoardId) => {
    editDraft((current) => takeOffTeam(current, teamId, userId, teamSize));
  };

  // `skip` as well as the loading flags: a skipped query reports isLoading
  // false, so without it a missing facility id rendered the full board over
  // empty data — "No teams yet", with an Add Team that POSTs to
  // /api/facility//leagues/... and 404s. Waiting on the league too, since
  // teamSize is unknown until it lands, and laying out a guess would flash a
  // 3s league as 2-slot cards and mark its half-built teams complete.
  if (
    skip ||
    isLoading ||
    rosterLoading ||
    leagueLoading ||
    (!readOnly && roundsLoading)
  ) {
    // Mirror the board's own shape — header, stat row, 2-up card grid, rail —
    // so the layout doesn't jump when the data lands.
    return (
      <div className="@container flex w-full flex-col gap-4">
        <Skeleton className="h-8 w-40 rounded-xl" />
        <div className="flex gap-2">
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} className="h-8 w-32 rounded-full" />
          ))}
        </div>
        <div className="flex flex-col gap-6 @min-[720px]:flex-row @min-[720px]:items-start">
          <div className="grid min-w-0 flex-1 grid-cols-2 gap-4">
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} className="h-36 w-full rounded-2xl" />
            ))}
          </div>
          <Skeleton className="h-64 w-full shrink-0 rounded-2xl @min-[720px]:w-56" />
        </div>
      </div>
    );
  }

  // The league loaded and its size isn't one the board can lay out. Refusing
  // to draw is the point: a guessed size silently mis-states which teams are
  // complete, and the operator has no way to tell.
  if (!teamSizeUsable) {
    return (
      <div className="flex w-full flex-col gap-4">
        {!hideHeading && (
          <h2 className="poppins text-xl font-bold tracking-tight text-text-primary">
            Teams
          </h2>
        )}
        <div
          role="status"
          className="flex items-start gap-3 rounded-xl border border-warn/35 bg-warn/10 p-4"
        >
          <span className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-surface text-warn">
            <TriangleAlert className="size-4" aria-hidden />
          </span>
          <div>
            <p className="poppins text-sm font-semibold text-warn">
              Teams can&rsquo;t be shown for this league
            </p>
            <p className="mulish mt-1 text-xs leading-relaxed text-text-primary">
              This league reports {String(teamSize)} players per team, which the
              board can&rsquo;t lay out — supported sizes are 1 to{" "}
              {MAX_TEAM_SIZE}. Building teams here could mark a team complete
              when it isn&rsquo;t. Check the league&rsquo;s format settings, or
              contact support.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex w-full flex-col gap-4">
      <RefreshOverlay active={isSaving} label="Teams building…">
      <TeamsBoard
        hideHeading={hideHeading}
        teamSize={teamSize}
        teams={displayed}
        players={players}
        unassigned={unassigned}
        isBusy={false}
        allowAddTeam={false}
        lockMessage={lockMessage}
        onAddTeam={handleAddTeam}
        onRenameTeam={handleRename}
        onDeleteTeam={setTeamToDelete}
        onWithdrawTeam={withdrawTeams ? setTeamToWithdraw : undefined}
        onRemovePlayer={handleRemovePlayer}
        onAssignPlayer={handleAssign}
      />
      </RefreshOverlay>

      <ConfirmDialog
        open={!!teamToWithdraw}
        onOpenChange={(open) => !open && setTeamToWithdraw(null)}
        title="Withdraw team?"
        description={WITHDRAW_TEAM_CONFIRMATION}
        confirmLabel="Withdraw team"
        onConfirm={() => void handleWithdraw()}
        isLoading={withdrawing}
      />
      <ConfirmDialog
        open={!!teamToDelete}
        onOpenChange={(o) => !o && setTeamToDelete(null)}
        title="Delete team?"
        description={`Delete ${teamToDelete?.name ?? "this team"}? Its player assignments are cleared; players stay on the league roster.`}
        confirmLabel="Delete"
        onConfirm={handleDelete}
        isLoading={deleting}
      />
    </div>
  );
});

/** Re-exported so callers can show the same counts the board does. */
export { boardStats };
